import React from 'react';
import { EstateProfile, PropertyAsset } from '../../types';
import { resolveResidenceForConsumers } from '../../server/services/rnrbResidenceFacts';
import { descendantInheritanceQualifies, isNonParentAgeTrust } from '../../server/services/rnrbInheritanceRoute';

interface Props {
    properties: PropertyAsset[];
    /** Residence-facts mode and nomination; omitted = an unstamped (legacy) estate. */
    ihtDetails?: EstateProfile['iht_details'];
}

export const PropertyRationalePanel: React.FC<Props> = ({ properties, ihtDetails }) => {
    if (!properties || properties.length === 0) return null;
    // The RNRB rationale follows the shared Stage-1 resolution only: never is_main_residence.
    const residence = resolveResidenceForConsumers(properties, ihtDetails);

    const getOwnershipLabel = (p: PropertyAsset) => {
        if (!p.ownership_type || p.ownership_type === 'unknown') return 'Unknown ownership';
        return p.ownership_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getProbateReason = (p: PropertyAsset): string => {
        if (p.ownership_type === 'sole') return 'Probate required to transfer or sell.';
        if (p.ownership_type === 'tenants_in_common') return 'Probate required for the deceased\'s share.';
        if (p.ownership_type === 'joint_tenants') return 'Passes by survivorship — probate usually not required.';
        return 'Probate depends on provider rules.';
    };

    const getRnrbReason = (p: PropertyAsset): string => {
        if (residence.qri === p) {
            const prefix = residence.provisional ? 'Used provisionally as the residence: confirm they lived in it as their home. ' : '';
            if (p.deceased_share_passes_to === 'spouse') return prefix + 'Left to spouse — spouse exemption applies, no RNRB needed yet.';
            if (p.deceased_share_passes_to === 'direct_descendants') {
                // D45: say "qualifies" only for a route the engine counts as inherited (s.8J), never for a trust it does not.
                if (descendantInheritanceQualifies(p.descendant_inheritance_type, p.bereaved_trust_origin)) return prefix + 'Passing to direct descendants — qualifies for the Residence Nil Rate Band.';
                if (p.descendant_inheritance_type === 'discretionary' || isNonParentAgeTrust(p.descendant_inheritance_type, p.bereaved_trust_origin)) {
                    return prefix + 'Passing to direct descendants on a trust that does not count as inheriting it — the Residence Nil Rate Band is not available on it.';
                }
                return prefix + 'Passing to direct descendants — whether it qualifies for the Residence Nil Rate Band depends on how they inherit it, which is not yet confirmed.';
            }
            return prefix + 'The residence for the RNRB — check inheritance for RNRB.';
        }
        if (residence.candidates.includes(p)) {
            if (residence.nominationRequired) return 'One of the possible residences — which one counts for the RNRB has not yet been nominated.';
            if (residence.mode === 'v2' && p.has_been_residence === 'yes') return 'They lived here as their home — whether it is the residence for the RNRB depends on the properties not yet answered, or on a nomination.';
            return 'Not yet known whether they lived here as their home — needed for the Residence Nil Rate Band.';
        }
        if (residence.mode === 'v2' && p.has_been_residence === 'no') return 'Not a home they lived in — does not qualify for the Residence Nil Rate Band.';
        return 'Not the residence used for the Residence Nil Rate Band.';
    };

    return (
        <div className="space-y-4">
            <h3 className="font-serif text-[22px] font-normal tracking-[-0.01em] text-[#1A1A1A] m-0 px-1">
                Properties considered
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
                {properties.map((p, i) => {
                    const probateReason = getProbateReason(p);
                    const rnrbReason = getRnrbReason(p);
                    const title = p.label || p.address?.split(',')[0] || `Property ${i + 1}`;

                    return (
                        <div key={p.id || i} className="bg-white border border-[#E8E4DB] p-5">
                            <h4 className="font-serif text-[18px] font-medium text-[#1A1A1A] m-0">
                                {title}
                            </h4>
                            <p className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mt-1 mb-3">
                                {getOwnershipLabel(p)} · <span className="font-mono tabular-nums normal-case tracking-normal">{p.deceased_share_percent}%</span> share
                            </p>

                            <p className="font-serif italic text-[13px] text-[#3F3D39] leading-relaxed mb-1.5">
                                {probateReason}
                            </p>
                            <p className="font-serif italic text-[13px] text-[#3F3D39] leading-relaxed">
                                {rnrbReason}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
